import type { ChartData } from 'chart.js'
import { IChatAnalyticsSummary, IChatDealerUsageReport, IChatPilotTarget } from './models'

const decimalFormatter = new Intl.NumberFormat('tr-TR', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 1,
})

const utcDateFormatter = new Intl.DateTimeFormat('tr-TR', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  timeZone: 'UTC',
})

export const formatPercent = (value: number): string => `%${decimalFormatter.format(value)}`

export const formatDuration = (ms: number): string => `${decimalFormatter.format(ms / 1000)} sn`

export const formatCount = (value: number): string => decimalFormatter.format(value)

/** Pilot hedefi: p95 yanıt süresi ≤ 10 sn (Hedef Durumu kartındaki `p95DurationSeconds` ile aynı eşik). */
export const P95_TARGET_MS = 10000

/** p95 açıklama kutusundaki hedef satırı: hedefte mi, değilse hedefin ne kadar üstünde. */
export const p95TargetStatus = (p95Ms: number): { passed: boolean; text: string } =>
  p95Ms <= P95_TARGET_MS
    ? { passed: true, text: `Hedefte: ${formatDuration(p95Ms)} ≤ ${formatDuration(P95_TARGET_MS)}` }
    : {
        passed: false,
        text: `Hedef altında: ${formatDuration(p95Ms)}, hedefin ${formatDuration(p95Ms - P95_TARGET_MS)} üstünde`,
      }

const percentTargetKeys = new Set(['technicalSuccess', 'feedbackPositiveRate', 'feedbackCoverage'])
const secondsTargetKeys = new Set(['p95DurationSeconds'])

export const formatTargetValue = (target: Pick<IChatPilotTarget, 'Key'>, value: number): string => {
  if (percentTargetKeys.has(target.Key)) return formatPercent(value)
  if (secondsTargetKeys.has(target.Key)) return `${decimalFormatter.format(value)} sn`
  return formatCount(value)
}

const dayPattern = /^(\d{4})-(\d{2})-(\d{2})$/

/**
 * Günlük grafik etiketi. Sunucu `Day` (`yyyy-MM-dd`, Türkiye günü) gönderiyorsa o kullanılır ve Date'e
 * ÇEVRİLMEZ (tarayıcı saat dilimi günü kaydırmasın); yoksa eski davranış: `DateUtc` UTC gününe göre.
 * `short` = gg.aa, `long` = gg.aa.yyyy.
 */
export const formatMetricDay = (
  metric: { DateUtc: string; Day?: string | null },
  style: 'short' | 'long' = 'long'
): string => {
  const match = dayPattern.exec(metric.Day ?? '')
  if (match) return style === 'short' ? `${match[3]}.${match[2]}` : `${match[3]}.${match[2]}.${match[1]}`
  const long = utcDateFormatter.format(new Date(metric.DateUtc))
  return style === 'short' ? long.slice(0, 5) : long
}

/** Sunucu yeni günlük soru/bayi alanlarını gönderiyor mu (eski backend'de grafik çizilmez). */
export const hasDailyQuestionMetrics = (summary: IChatAnalyticsSummary): boolean =>
  summary.DailyTrend.some(
    metric =>
      metric.TypedQuestions !== undefined || metric.ButtonClicks !== undefined || metric.ActiveDealers !== undefined
  )

/**
 * Günlük soru ve bayi grafiği (Faz-37 §1a): yazılı soru + buton tıklaması YIĞILMIŞ sütun, aktif bayi
 * ikinci eksende (`y1`) çizgi. Karma grafik `Bar` bileşeniyle çizilir; çizgi serisi `type: 'line'` taşır.
 */
export const buildDailyQuestionChart = (
  summary: IChatAnalyticsSummary
): ChartData<'bar' | 'line', number[], string> => ({
  labels: summary.DailyTrend.map(metric => formatMetricDay(metric, 'short')),
  datasets: [
    {
      type: 'bar' as const,
      label: 'Yazılı Soru',
      data: summary.DailyTrend.map(metric => metric.TypedQuestions ?? 0),
      backgroundColor: '#2f6fed',
      stack: 'questions',
      yAxisID: 'y',
      order: 2,
    },
    {
      type: 'bar' as const,
      label: 'Buton Tıklaması',
      data: summary.DailyTrend.map(metric => metric.ButtonClicks ?? 0),
      backgroundColor: '#9ab8f5',
      stack: 'questions',
      yAxisID: 'y',
      order: 2,
    },
    {
      type: 'line' as const,
      label: 'Aktif Bayi',
      data: summary.DailyTrend.map(metric => metric.ActiveDealers ?? 0),
      borderColor: '#f0a202',
      backgroundColor: 'rgba(240, 162, 2, 0.2)',
      yAxisID: 'y1',
      tension: 0.25,
      order: 1,
    },
  ],
})

export const buildDailyChart = (summary: IChatAnalyticsSummary): ChartData<'line', number[], string> => ({
  labels: summary.DailyTrend.map(metric => formatMetricDay(metric)),
  datasets: [
    {
      label: 'Toplam',
      data: summary.DailyTrend.map(metric => metric.Total),
      borderColor: '#2f6fed',
      backgroundColor: 'rgba(47, 111, 237, 0.16)',
    },
    {
      label: 'Teknik Başarılı',
      data: summary.DailyTrend.map(metric => metric.TechnicalSuccess),
      borderColor: '#239b56',
      backgroundColor: 'rgba(35, 155, 86, 0.16)',
    },
    {
      label: 'Hata',
      data: summary.DailyTrend.map(metric => metric.Error),
      borderColor: '#d64545',
      backgroundColor: 'rgba(214, 69, 69, 0.16)',
    },
  ],
})

export const buildOutcomeChart = (summary: IChatAnalyticsSummary): ChartData<'doughnut', number[], string> => ({
  labels: summary.OutcomeBreakdown.map(item => item.Key),
  datasets: [
    {
      label: 'Adet',
      data: summary.OutcomeBreakdown.map(item => item.Count),
      backgroundColor: ['#239b56', '#f0a202', '#d64545', '#718096', '#805ad5', '#2b6cb0'],
    },
  ],
})

export const buildInputModeChart = (summary: IChatAnalyticsSummary): ChartData<'bar', number[], string> => ({
  labels: summary.InputModeBreakdown.map(item => item.Key),
  datasets: [
    {
      label: 'Adet',
      data: summary.InputModeBreakdown.map(item => item.Count),
      backgroundColor: ['#2f6fed', '#805ad5'],
    },
  ],
})

export const targetClass = (passed: boolean): 'passed' | 'failed' => (passed ? 'passed' : 'failed')

export const startOfLocalDay = (date: Date): Date => {
  const start = new Date(date)
  start.setHours(0, 0, 0, 0)
  return start
}

export const endExclusiveOfLocalDay = (date: Date): Date => {
  const endExclusive = startOfLocalDay(date)
  endExclusive.setDate(endExclusive.getDate() + 1)
  return endExclusive
}

/** Sunucunun izin verdiği en uzun aralık (gün, bitiş dahil); backend aynı sınırı uygular. */
export const MAX_RANGE_DAYS = 90

const DAY_MS = 24 * 60 * 60 * 1000

/** Seçilen aralık geçersizse ekranda gösterilecek metin, geçerliyse `null`. */
export const dateRangeError = (start: Date, end: Date): string | null => {
  const days = Math.round((startOfLocalDay(end).getTime() - startOfLocalDay(start).getTime()) / DAY_MS) + 1
  if (days < 1) return 'Başlangıç tarihi bitiş tarihinden sonra olamaz.'
  if (days > MAX_RANGE_DAYS) return `En fazla ${MAX_RANGE_DAYS} günlük aralık sorgulanabilir.`
  return null
}

export const DEFAULT_LOAD_ERROR_TEXT = 'Lütfen tekrar deneyin.'

/**
 * Yükleme hatası → ekrandaki metin. Yalnız ClientProxy'nin fırlattığı `{ description }` (sunucunun
 * kullanıcıya dönük Message'ı, ör. 90 gün sınırı) gösterilir; ham `Error.message` teknik detay
 * sızdırabileceği için kullanılmaz.
 */
export const loadErrorMessage = (error: unknown, fallback: string = DEFAULT_LOAD_ERROR_TEXT): string => {
  if (!error || typeof error !== 'object' || error instanceof Error) return fallback
  const description = (error as { description?: unknown }).description
  return typeof description === 'string' && description.trim() && description !== 'OK' ? description : fallback
}

const pad2 = (value: number): string => `${value}`.padStart(2, '0')

/** `InputCampaign type='date'` değeri: yerel takvime göre `YYYY-MM-DD` (UTC'ye kaydırılmaz). */
export const toDateInputValue = (date: Date): string =>
  `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`

const dateInputPattern = /^(\d{4})-(\d{2})-(\d{2})$/

/**
 * `YYYY-MM-DD` → yerel gün başı. Boş, biçimsiz veya takvimde olmayan gün (31 Şubat) `null` döner;
 * çağıran taslağı değiştirmez.
 */
export const fromDateInputValue = (value: string): Date | null => {
  const match = dateInputPattern.exec(value ?? '')
  if (!match) return null

  const year = Number(match[1])
  const month = Number(match[2])
  const day = Number(match[3])
  const date = new Date(year, month - 1, day)
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) return null

  return date
}

const localDateTimeFormatter = new Intl.DateTimeFormat('tr-TR', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
})

/** Geri bildirim/soru zamanı: yerel saatle gün + saat (gün düzeyi değil, olay zamanı). */
export const formatLocalDateTime = (iso: string): string => localDateTimeFormatter.format(new Date(iso))

/** Sorular sekmesi oy sütunu: 👍 / 👎 / – (oysuz veya tanınmayan değer). */
export const voteIcon = (vote: string | null | undefined): string => {
  if (vote === 'up') return '👍'
  if (vote === 'down') return '👎'
  return '–'
}

type QuestionSourceFields = { Question: string | null; QuestionSource: string | null; ButtonIntent: string | null }

export const isButtonQuestion = (row: Pick<QuestionSourceFields, 'QuestionSource'>): boolean =>
  row.QuestionSource === 'button'

/** "Nasıl soruldu" rozeti / Excel değeri. */
export const questionSourceLabel = (row: Pick<QuestionSourceFields, 'QuestionSource'>): string =>
  isButtonQuestion(row) ? 'Buton' : 'Yazılı'

/** Sunucu butonun Türkçe adını bilmiyorsa `Question` olarak intent kodunu gönderir. */
export const isUnlabeledButton = (row: QuestionSourceFields): boolean =>
  isButtonQuestion(row) && !!row.ButtonIntent && row.Question === row.ButtonIntent

export const UNLABELED_BUTTON_TITLE = 'Buton adı tanımlı değil'

const feedbackReasonLabels: Record<string, string> = {
  misunderstood: 'Sorumu yanlış anladı',
  incomplete: 'Bilgi eksikti',
  incorrect: 'Bilgi yanlıştı',
  slow: 'Çok yavaştı',
  other: 'Diğer',
  belirtilmedi: 'Belirtilmedi',
}

/** Bilinmeyen kod SESSİZCE DÜŞÜRÜLMEZ: ham kod gösterilir (sunucu kümesi büyürse fark edilir). */
export const feedbackReasonLabel = (code: string): string => feedbackReasonLabels[code] ?? code

export const buildFeedbackTrendChart = (
  summary: IChatAnalyticsSummary
): ChartData<'line', number[], string> => ({
  labels: summary.Feedback.DailyTrend.map(metric => formatMetricDay(metric)),
  datasets: [
    {
      label: 'Olumlu',
      data: summary.Feedback.DailyTrend.map(metric => metric.Positive),
      borderColor: '#239b56',
      backgroundColor: 'rgba(35, 155, 86, 0.16)',
    },
    {
      label: 'Olumsuz',
      data: summary.Feedback.DailyTrend.map(metric => metric.Negative),
      borderColor: '#d64545',
      backgroundColor: 'rgba(214, 69, 69, 0.16)',
    },
  ],
})

export const buildFeedbackReasonChart = (
  summary: IChatAnalyticsSummary
): ChartData<'bar', number[], string> => ({
  labels: summary.Feedback.ReasonBreakdown.map(item => feedbackReasonLabel(item.Key)),
  datasets: [
    {
      label: 'Adet',
      data: summary.Feedback.ReasonBreakdown.map(item => item.Count),
      backgroundColor: '#d64545',
    },
  ],
})

/* --- Bayi & Kullanıcı sekmesi grafikleri (ADR-017) --- */

export const buildDailyActiveChart = (
  report: IChatDealerUsageReport
): ChartData<'line', number[], string> => ({
  labels: report.DailyActiveDealers.map(metric => utcDateFormatter.format(new Date(metric.DateUtc))),
  datasets: [
    {
      label: 'Aktif Bayi',
      data: report.DailyActiveDealers.map(metric => metric.DealerCount),
      borderColor: '#2f6fed',
      backgroundColor: 'rgba(47, 111, 237, 0.16)',
    },
    {
      label: 'Aktif Kullanıcı',
      data: report.DailyActiveDealers.map(metric => metric.UserCount),
      borderColor: '#239b56',
      backgroundColor: 'rgba(35, 155, 86, 0.16)',
    },
  ],
})

/** Ünvanı çözülemeyen bayi etikette SAP koduyla görünür; satır düşürülmez. */
export const buildTopDealersChart = (
  report: IChatDealerUsageReport
): ChartData<'bar', number[], string> => ({
  labels: report.TopDealersByTurns.map(item => item.DealerName ?? item.DealerSapCode),
  datasets: [
    {
      label: 'Tur',
      data: report.TopDealersByTurns.map(item => item.TurnCount),
      backgroundColor: '#2f6fed',
    },
  ],
})
